import { useEffect, useState, useCallback, useRef } from 'react';

export type TimeFormat = 'detail' | 'short';

export interface TimeDetails {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  isExpired: boolean;
  formattedDate?: string;
}

export const useTimeFormatter = (
  endDate: string,
  format: TimeFormat = 'short',
): string | TimeDetails => {
  const [timeDetails, setTimeDetails] = useState<TimeDetails>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    isExpired: false,
  });

  const formatRef = useRef(format);
  formatRef.current = format;

  const updateTime = useCallback(() => {
    if (!endDate) {
      setTimeDetails((prev) =>
        prev.isExpired
          ? prev
          : {
              days: 0,
              hours: 0,
              minutes: 0,
              seconds: 0,
              isExpired: false,
            },
      );
      return;
    }

    let end: Date;
    if (endDate.includes('Z')) {
      end = new Date(endDate);
    } else {
      const cleanDate = endDate.split('.')[0];
      end = new Date(cleanDate + 'Z');
    }

    if (isNaN(end.getTime())) {
      setTimeDetails((prev) =>
        prev.isExpired
          ? prev
          : {
              days: 0,
              hours: 0,
              minutes: 0,
              seconds: 0,
              isExpired: false,
            },
      );
      return;
    }

    const now = new Date();
    const diffMs = end.getTime() - now.getTime();

    if (diffMs <= 0) {
      const day = String(end.getUTCDate()).padStart(2, '0');
      const month = String(end.getUTCMonth() + 1).padStart(2, '0');
      const year = end.getUTCFullYear();
      setTimeDetails((prev) =>
        prev.isExpired
          ? prev
          : {
              days: 0,
              hours: 0,
              minutes: 0,
              seconds: 0,
              isExpired: true,
              formattedDate: `${day}.${month}.${year}`,
            },
      );
      return;
    }

    const totalSeconds = Math.floor(diffMs / 1000);
    const days = Math.floor(totalSeconds / (24 * 60 * 60));
    const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60));
    const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
    const seconds = totalSeconds % 60;

    setTimeDetails((prev) => {
      if (
        prev.days === days &&
        prev.hours === hours &&
        prev.minutes === minutes &&
        prev.seconds === seconds
      ) {
        return prev;
      }
      return {
        days,
        hours,
        minutes,
        seconds,
        isExpired: false,
      };
    });
  }, [endDate]);

  useEffect(() => {
    updateTime();
    const intervalId = setInterval(updateTime, 1000);
    return () => clearInterval(intervalId);
  }, [updateTime]);

  if (timeDetails.isExpired) {
    return 'Таймер обновлен';
  }

  if (formatRef.current === 'detail') {
    return timeDetails;
  }

  if (timeDetails.days > 0) {
    return `${timeDetails.days}д ${timeDetails.hours}ч ${timeDetails.minutes}м`;
  }

  if (timeDetails.hours > 0) {
    return `${timeDetails.hours}ч ${timeDetails.minutes}м ${timeDetails.seconds}с`;
  }

  return `${timeDetails.minutes}м ${timeDetails.seconds}с`;
};
